import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Loader2, ArrowRight, Clock, Star, Download, MessageSquare } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const DEVIS_STATUS_LABELS = {
    creation: 'Création',
    a_envoyer: 'À envoyer',
    envoye: 'Envoyé',
    relance_1: 'Relancé 1',
    relance_2: 'Relancé 2',
    relance_3: 'Relancé 3',
    interesse: 'Intéressé',
    accepte: 'Accepté',
    refuse: 'Refusé',
    sans_reponse: 'Sans réponse',
};

const DEVIS_STATUS_COLORS = {
    creation: 'bg-slate-100 text-slate-700 border-slate-200',
    a_envoyer: 'bg-amber-100 text-amber-700 border-amber-200',
    envoye: 'bg-blue-100 text-blue-700 border-blue-200',
    relance_1: 'bg-orange-100 text-orange-700 border-orange-200',
    relance_2: 'bg-orange-100 text-orange-700 border-orange-200',
    relance_3: 'bg-red-100 text-red-700 border-red-200',
    interesse: 'bg-cyan-100 text-cyan-700 border-cyan-200',
    accepte: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    refuse: 'bg-red-100 text-red-700 border-red-200',
    sans_reponse: 'bg-slate-100 text-slate-600 border-slate-200',
};

function formatHistoryDate(dateStr) {
    if (!dateStr) return '-';
    try {
        const d = new Date(dateStr);
        return d.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch {
        return '-';
    }
}

export function DevisStatusHistoryDialog({ open, onOpenChange, devis }) {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open && devis) {
            setLoading(true);
            if (devis.id) {
                // Single devis history
                axios.get(`${API}/api/devis/${devis.id}/status-history`, { withCredentials: true })
                    .then(({ data }) => setHistory(data))
                    .catch(() => setHistory([]))
                    .finally(() => setLoading(false));
            } else if (devis.inventaire_id) {
                // Dossier history: fetch all devis then merge their histories
                axios.get(`${API}/api/devis/dossier/${devis.inventaire_id}`, { withCredentials: true })
                    .then(async ({ data: devisList }) => {
                        const allHistory = [];
                        for (const d of devisList) {
                            try {
                                const { data: h } = await axios.get(`${API}/api/devis/${d.id}/status-history`, { withCredentials: true });
                                h.forEach(entry => { entry.formule_label = d.formule_label || d.formule; });
                                allHistory.push(...h);
                            } catch {}
                        }
                        allHistory.sort((a, b) => new Date(b.changed_at || 0) - new Date(a.changed_at || 0));
                        setHistory(allHistory);
                    })
                    .catch(() => setHistory([]))
                    .finally(() => setLoading(false));
            } else {
                setHistory([]);
                setLoading(false);
            }
        }
    }, [open, devis?.id, devis?.inventaire_id]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-y-auto" data-testid="devis-status-history-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-lg">
                        <Star size={20} className="text-[#2e7cd6] fill-[#2e7cd6]" />
                        Historique des statuts — {devis?.client_nom}
                    </DialogTitle>
                </DialogHeader>

                {loading ? (
                    <div className="flex items-center justify-center py-10">
                        <Loader2 className="h-6 w-6 animate-spin text-[#2e7cd6]" />
                    </div>
                ) : history.length === 0 ? (
                    <div className="text-center py-10 text-slate-400 text-sm">
                        Aucun changement de statut enregistré
                    </div>
                ) : (
                    <div className="relative pl-6 mt-2">
                        {/* Timeline line */}
                        <div className="absolute left-[11px] top-2 bottom-2 w-0.5 bg-slate-200" />

                        <div className="space-y-5">
                            {history.map((entry, idx) => (
                                <div key={idx} className="relative flex items-start gap-3" data-testid={`devis-history-entry-${idx}`}>
                                    {/* Timeline dot */}
                                    <div className={`absolute -left-6 top-1 w-3 h-3 rounded-full border-2 ${idx === 0 ? 'bg-[#2e7cd6] border-[#2e7cd6]' : 'bg-white border-slate-300'}`} />

                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            {entry.ancien_statut ? (
                                                <>
                                                    <Badge variant="outline" className={`${DEVIS_STATUS_COLORS[entry.ancien_statut] || 'bg-slate-100 text-slate-700'} border text-xs`}>
                                                        {DEVIS_STATUS_LABELS[entry.ancien_statut] || entry.ancien_statut}
                                                    </Badge>
                                                    <ArrowRight size={14} className="text-slate-400 shrink-0" />
                                                </>
                                            ) : null}
                                            <Badge variant="outline" className={`${DEVIS_STATUS_COLORS[entry.nouveau_statut] || 'bg-slate-100 text-slate-700'} border text-xs`}>
                                                {DEVIS_STATUS_LABELS[entry.nouveau_statut] || entry.nouveau_statut}
                                            </Badge>

                                            <span className="text-slate-300 mx-1">|</span>
                                            <Clock size={12} className="text-slate-400 shrink-0" />
                                            <span className="text-xs text-slate-400">{formatHistoryDate(entry.changed_at)}</span>
                                            <span className="text-slate-300">|</span>
                                            <span className="text-xs text-slate-400">{entry.changed_by || 'Système'}</span>
                                        </div>
                                        {entry.justificatif && (
                                            <a
                                                href={`${API}${entry.justificatif.url}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="inline-flex items-center gap-1.5 mt-1.5 px-2 py-1 rounded bg-blue-50 text-xs text-[#2e7cd6] hover:bg-blue-100 transition-colors"
                                                data-testid={`history-justificatif-${idx}`}
                                            >
                                                <Download size={12} />
                                                Justificatif : {entry.justificatif.filename}
                                            </a>
                                        )}
                                        {entry.remarque && (
                                            <div className="flex items-start gap-1.5 mt-1.5 px-2 py-1.5 rounded bg-amber-50 border border-amber-100" data-testid={`history-remarque-${idx}`}>
                                                <MessageSquare size={12} className="text-amber-600 mt-0.5 shrink-0" />
                                                <span className="text-xs text-amber-800">{entry.remarque}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
